// Fatia S5b: visão consolidada dos clientes da agência, ordenada por "precisa de atenção".
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Search } from 'lucide-react'
import { brl, num } from '@impulso/shared'
import { useSession } from '../../lib/session'
import { Empty, ErrorState, Loading, PageHead, Tabs } from '../../ui'
import { useAgencyOverview } from '../../features/agency/api'
import { ClientCard } from '../../features/agency/ClientCard'

const MONTHS = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro']

export default function Overview() {
  const { me } = useSession()
  const agencies = (me?.accounts ?? []).filter((a) => a.kind === 'agency')
  const [accountId, setAccountId] = useState<string | undefined>(undefined)
  const account = agencies.find((a) => a.id === accountId) ?? agencies[0]
  const q = useAgencyOverview(account?.id)
  const [filter, setFilter] = useState<'all' | 'attention'>('all')
  const [search, setSearch] = useState('')

  const clients = useMemo(() => {
    const all = q.data?.clients ?? []
    const s = search.trim().toLowerCase()
    return all.filter((c) => (filter === 'all' || c.alerts.length > 0) && (!s || c.name.toLowerCase().includes(s)))
  }, [q.data, filter, search])

  if (!account) {
    return (
      <>
        <PageHead title="Visão dos clientes" />
        <Empty title="Esta visão é para agências">Quando sua conta for de agência, aqui aparecem todos os clientes num só lugar.</Empty>
      </>
    )
  }

  const canAdd = account.permissions.includes('account.manage_companies')
  const addLink = `/app/conta/${account.id}/empresas`
  const monthLabel = q.data ? `${MONTHS[Number(q.data.month.slice(5, 7)) - 1]}` : ''

  return (
    <>
      <PageHead title="Visão dos clientes" sub={q.data ? `${account.name} · ${monthLabel}, dia ${q.data.dayOfMonth} de ${q.data.daysInMonth}` : account.name}>
        {agencies.length > 1 && (
          <select className="select" style={{ width: 'auto' }} aria-label="Escolher agência" value={account.id} onChange={(e) => setAccountId(e.target.value)}>
            {agencies.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
        {canAdd && <Link className="btn primary" to={addLink}><Plus size={16} /> Adicionar cliente</Link>}
      </PageHead>

      {q.isLoading && <div className="card"><Loading lines={6} /></div>}
      {q.error && <ErrorState error={q.error} onRetry={() => q.refetch()} />}

      {q.data && q.data.clients.length === 0 && (
        <Empty title="Nenhum cliente por aqui ainda">
          <p>Cadastre a primeira empresa-cliente para acompanhar gasto, resultados e alertas de todos num só lugar.</p>
          {canAdd ? <Link className="btn primary mt-12" to={addLink}><Plus size={16} /> Adicionar cliente</Link> : <p className="small mt-8">Peça a um administrador da agência para dar acesso aos clientes.</p>}
        </Empty>
      )}

      {q.data && q.data.clients.length > 0 && (
        <div className="stack">
          <div className="grid g-4">
            <Kpi label="Clientes" value={num(q.data.totals.companies)} hint={`${q.data.totals.needingAttention} precisa${q.data.totals.needingAttention === 1 ? '' : 'm'} de atenção`} />
            <Kpi label="Investido no mês" value={brl(q.data.totals.spendMonth)} hint={q.data.totals.monthlyBudget ? `de ${brl(q.data.totals.monthlyBudget)} em orçamentos` : 'Sem orçamentos definidos'} />
            <Kpi label="Resultados no mês" value={num(q.data.totals.results)} hint="Conversas, contatos e vendas" />
            <Kpi label="Sugestões abertas" value={num(q.data.totals.openSuggestions)} hint="Esperando uma decisão" />
          </div>

          <div className="row wrap between" style={{ gap: 12 }}>
            <Tabs
              value={filter}
              onChange={setFilter}
              options={[{ value: 'all', label: `Todos (${q.data.clients.length})` }, { value: 'attention', label: `Precisam de atenção (${q.data.totals.needingAttention})` }]}
            />
            <label className="row" style={{ gap: 8, flex: '1 1 200px', maxWidth: 320 }}>
              <Search size={16} className="muted" />
              <input className="input" placeholder="Buscar cliente" aria-label="Buscar cliente" value={search} onChange={(e) => setSearch(e.target.value)} />
            </label>
          </div>

          {clients.length === 0 ? (
            <Empty title={filter === 'attention' ? 'Nenhum cliente precisando de atenção' : 'Nenhum cliente encontrado'}>
              {filter === 'attention' ? 'Tudo certo com todos os clientes agora.' : 'Tente outro nome.'}
            </Empty>
          ) : (
            <div className="grid g-3">
              {clients.map((c) => <ClientCard key={c.companyId} c={c} dayOfMonth={q.data!.dayOfMonth} daysInMonth={q.data!.daysInMonth} />)}
            </div>
          )}
        </div>
      )}
    </>
  )
}

function Kpi({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="card kpi">
      <div className="label">{label}</div>
      <div className="value">{value}</div>
      <div className="hint">{hint}</div>
    </div>
  )
}
